import {
  type DoppioPlayer,
  type TDoppioPlayerConfig,
  type TResolutionsLimits,
} from '@streaming/doppio-player';
import { TDeepPartial, AsyncGuarderDecorator, TLogLevel } from '@streaming/mouflon-utils';
import {
  type Player as RistrettoPlayer,
  type Broadcaster,
  type IRistrettoPlayerConfig,
} from '@streaming/ristretto-player';

import { INativePlayerConfig, IStreamConfig } from '@customTypes/common';
import { MetricsCollector } from '@metrics/Metrics';
import { EPlayersEvents } from '@players/Players.events';
import { type TWebRTCAbrPreset } from '@players/RistrettoPlaying';
import { TMMPEventBus } from '@utils/EventBus';

export abstract class AbstractWrapper<
  TConfig,
  TStreamConfig,
  TPlayerType extends DoppioPlayer | RistrettoPlayer | Broadcaster,
> {
  protected _videoElement: HTMLVideoElement | null = null;
  protected _lastKnownConfig: TDeepPartial<TConfig> = {} as TDeepPartial<TConfig>;
  protected _lastKnownStreamConfig: TStreamConfig | null = null;
  protected _playerInstance: TPlayerType | null = null;
  protected _eventBusInstance: TMMPEventBus;
  protected _isStarted: boolean;
  protected _isAbrEnabled: boolean;
  protected _appKey: string;
  protected _metricsCollector: MetricsCollector | null = null;
  protected _persistent: boolean;

  protected constructor(eventBusInstance: TMMPEventBus, options: { persistent?: boolean } = {}) {
    this._eventBusInstance = eventBusInstance;
    this._eventBusInstance.publish(EPlayersEvents.MODULE_LOADED, {} as never);
    this._persistent = !!options.persistent;
  }

  public setMetricsCollector(metricsCollector: MetricsCollector): void {
    this._metricsCollector = metricsCollector;
  }

  public destroy(): AbstractWrapper<TConfig, TStreamConfig, TPlayerType> {
    this._destroyPlayerInstance();
    this._isStarted = false;

    return this;
  }

  public setAppKey(appKey: string): AbstractWrapper<TConfig, TStreamConfig, TPlayerType> {
    this._appKey = appKey;

    return this;
  }

  public setVideoElement(
    videoElement: HTMLVideoElement | null,
  ): AbstractWrapper<TConfig, TStreamConfig, TPlayerType> {
    this._videoElement = videoElement;

    if (videoElement) {
      this._playerInstance?.setVideoElement(videoElement);
    }

    return this;
  }

  @AsyncGuarderDecorator
  public async recreatePlayerInstance(): Promise<void> {
    if (this._playerInstance) {
      this.destroy();
      this._playerInstance = null;
    }

    await this._createPlayerInstance();
  }

  public start(): AbstractWrapper<TConfig, TStreamConfig, TPlayerType> {
    if (!this._playerInstance) {
      this.recreatePlayerInstance();
    }

    this._playerInstance?.start();
    this._isStarted = true;

    return this;
  }

  public stop(): AbstractWrapper<TConfig, TStreamConfig, TPlayerType> {
    this._isStarted = false;
    if (this._persistent && this._playerInstance && 'stop' in this._playerInstance) {
      this._playerInstance.stop();
    } else {
      this._destroyPlayerInstance();
    }

    return this;
  }

  public setPlayerConfig(
    config: TDeepPartial<TConfig>,
  ): AbstractWrapper<TConfig, TStreamConfig, TPlayerType> {
    this._lastKnownConfig = config;
    this._playerInstance?.setConfig(
      config as TDeepPartial<TDoppioPlayerConfig & IRistrettoPlayerConfig & INativePlayerConfig>,
      true,
    );

    return this;
  }

  public abstract setStreamConfig(
    config?: IStreamConfig,
  ): AbstractWrapper<TConfig, TStreamConfig, TPlayerType>;
  public abstract enableAbr(): AbstractWrapper<TConfig, TStreamConfig, TPlayerType>;
  public abstract disableAbr(): AbstractWrapper<TConfig, TStreamConfig, TPlayerType>;
  public abstract setResolutionByName(
    resolutionName: string,
  ): AbstractWrapper<TConfig, TStreamConfig, TPlayerType>;
  public abstract setResolutionLimits(
    limits: TResolutionsLimits,
  ): AbstractWrapper<TConfig, TStreamConfig, TPlayerType>;

  protected _destroyPlayerInstance(): void {
    if (!this._playerInstance) {
      return;
    }

    this._detachEventListeners();
    this._playerInstance.destroy();
    this._playerInstance = null;
  }

  protected abstract _attachEventListeners(): void;
  protected abstract _detachEventListeners(): void;
  protected abstract _createPlayerInstance(): Promise<void>;
  public abstract setLoggerLevel(levels: TLogLevel[]): void;
  public abstract setAbrResolutions(
    resolutions: TWebRTCAbrPreset[],
  ): AbstractWrapper<TConfig, TStreamConfig, TPlayerType>;
}
